/**
 * PATH: packages/job-hub/src/jobs/client/resolveMoneyRoutes.ts
 * FILE_ID: JOBHUB-JOBS-CLIENT-MONEYRT-001
 * PURPOSE: Route-table segment 0 — Rank #3/#4 money-document ledger routes:
 *          org job-money-config read, per-job ledger document list, and the
 *          ledger PDF/DOCX downloads (claims app endpoints, NOT report/).
 * WHY: Rank #4 follow-up (complete dead buttons): the shared create modal
 *      gates its CTAs on the org's job_money_config.path, and Django hosts
 *      get real artifact downloads for advertised ledger actions. LANDMINE:
 *      must run BEFORE
 *      resolveJobRoutes so /jobs/:id/money-documents beats the bare
 *      /jobs/:id catch-all.
 */
import type {
  MoneyDocumentAction,
  MoneyDocumentActionSource,
  MoneyDocumentArtifact,
} from '@quotech/api/moneyDocuments'
import type { SegmentResolver } from './routeTypes'
import { ID } from './routeTypes'

/** One ledger document row in the hub's shape. Pure reshape target. */
export interface MoneyDocumentRow extends MoneyDocumentActionSource {
  id: string
  doc_type: 'claim' | 'invoice'
  source: 'native' | 'external' | string
  number: number | null
  status: string
  status_display: string
  period: string | null
  amount_ex_gst: number | null
  amount_inc_gst: number | null
  created_at: string | null
  xero_invoice_number: string | null
  latest_artifact: MoneyDocumentArtifact | null
}

function num(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

type Row = Record<string, unknown> & {
  xero_link?: { xero_invoice_number?: string | null } | null
}

function actions(value: unknown): MoneyDocumentAction[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value.filter((item): item is string => typeof item === 'string')
}

/** Paginated /api/claims/by-job/ payload -> MoneyDocumentRow[]. Pure I->O. */
export function reshapeMoneyDocuments(data: unknown): MoneyDocumentRow[] {
  const rows: Row[] = Array.isArray(data)
    ? (data as Row[])
    : (((data as { results?: Row[] })?.results ?? []) as Row[])
  return rows.map((r) => ({
    id: String(r.id ?? ''),
    doc_type: r.doc_type === 'invoice' ? 'invoice' : 'claim',
    source: String(r.source ?? 'native'),
    number: num(r.claim_number),
    status: String(r.status ?? ''),
    status_display: String(r.status_display ?? r.status ?? ''),
    period: (r.period as string | null | undefined) ?? null,
    amount_ex_gst: num(r.this_claim),
    amount_inc_gst: num(r.this_claim_incl_gst),
    created_at: (r.created_at as string | null | undefined) ?? null,
    xero_invoice_number:
      r.xero_link?.xero_invoice_number ??
      ((r.xero_invoice_number as string | null | undefined) ?? null),
    available_actions: actions(r.available_actions),
    latest_artifact:
      r.latest_artifact && typeof r.latest_artifact === 'object'
        ? (r.latest_artifact as MoneyDocumentArtifact)
        : null,
  }))
}

export const resolveMoneyRoutes: SegmentResolver = ({ verb, match, tenantParam, requestParams }) => {
  // Org money-document config (path/depth/template). No org context ->
  // synthetic null so consumers fall back to today's default (both paths).
  if (match('/org/job-money-config') && verb === 'get') {
    const orgId = tenantParam?.tenant
    if (orgId == null) return { kind: 'synthetic', synthetic: null }
    return {
      kind: 'http',
      method: 'get',
      url: `/api/organisations/${orgId}/job-money-config/`,
    }
  }

  // Ledger documents issued against a job (claims app, Rank #3 spine).
  let m = match(`/jobs/${ID}/money-documents`)
  if (m && verb === 'get') {
    return {
      kind: 'http',
      method: 'get',
      url: `/api/claims/by-job/${m[1]}/`,
      reshape: reshapeMoneyDocuments,
    }
  }

  m = match(`/jobs/${ID}/settlements`)
  if (m && verb === 'get') {
    return {
      kind: 'http',
      method: 'get',
      url: `/api/claims/settlements/jobs/${m[1]}/position/`,
    }
  }

  m = match(`/jobs/${ID}/receipts`)
  if (m && verb === 'post') {
    return {
      kind: 'http',
      method: 'post',
      url: `/api/claims/settlements/jobs/${m[1]}/receipts/`,
    }
  }

  m = match(`/receipts/${ID}/allocations`)
  if (m && verb === 'post') {
    return {
      kind: 'http',
      method: 'post',
      url: `/api/claims/settlements/receipts/${m[1]}/allocations/`,
    }
  }

  // Native Quotech document position. This deliberately differs from the
  // report.Invoice mirror: settlement position lives on the dedicated route.
  m = match(`/jobs/${ID}/financial-position`)
  if (m && verb === 'get') {
    const scope = requestParams?.scope === 'inclusive' ? 'inclusive' : 'specific'
    return {
      kind: 'http',
      method: 'get',
      url: '/api/claims/financial-position/',
      params: { job: m[1], scope },
    }
  }

  // Ledger PDF — the claims app renders it server-side; callers pass
  // responseType 'blob' and the adapter forwards the config untouched.
  m = match(`/money-documents/${ID}/pdf`)
  if (m && verb === 'get') {
    return { kind: 'http', method: 'get', url: `/api/claims/${m[1]}/export_pdf/` }
  }

  // Native DOCX is capability-gated by the row's available_actions in the
  // panel. Full-document /invoices/:id/generate-docx routes stay untouched.
  m = match(`/money-documents/${ID}/docx`)
  if (m && verb === 'post') {
    return { kind: 'http', method: 'post', url: `/api/claims/${m[1]}/generate_docx/` }
  }

  m = match(`/money-documents/${ID}/source`)
  if (m && verb === 'get') {
    return {
      kind: 'http',
      method: 'get',
      url: `/api/claims/${m[1]}/download-source/`,
    }
  }

  return null
}
